import { createAction, props } from '@ngrx/store';
import { Session } from '@ory/client';

import { OryFlowType } from '../constants/ory.constant';
import {
  AuthError,
  LoginFlow,
  LoginFormData,
  OtpFormData,
  RegistrationFlow,
  RegistrationFormData,
} from './ory-auth.models';

const actionPrefix = '[Ory Auth]';
export const loadSession = createAction(`${actionPrefix} Load Session`);

export const loadSessionSuccess = createAction(
  `${actionPrefix} Load Session Success`,
  props<{ session: Session }>(),
);

export const loadSessionFailure = createAction(
  `${actionPrefix} Load Session Failure`,
  props<{ error: AuthError }>(),
);

export const clearSession = createAction(`${actionPrefix} Clear Session`);

export const startLoginFlow = createAction(
  `${actionPrefix} Start Login Flow`,
  props<{ redirectTo?: string | null }>(),
);

export const startLoginFlowSuccess = createAction(
  `${actionPrefix} Start Login Flow Success`,
  props<{ flowId: string; csrfToken: string }>(),
);

export const startLoginFlowFailure = createAction(
  `${actionPrefix} Start Login Flow Failure`,
  props<{ error: AuthError }>(),
);

export const loadLoginFlow = createAction(
  `${actionPrefix} Load Login Flow`,
  props<{ flowId: string }>(),
);

export const loadLoginFlowSuccess = createAction(
  `${actionPrefix} Load Login Flow Success`,
  props<{ flow: LoginFlow; csrfToken: string }>(),
);

export const loadLoginFlowFailure = createAction(
  `${actionPrefix} Load Login Flow Failure`,
  props<{ error: AuthError }>(),
);

export const submitLogin = createAction(
  `${actionPrefix} Submit Login`,
  props<{ flowId: string; formData: LoginFormData }>(),
);

export const submitLoginSuccess = createAction(
  `${actionPrefix} Submit Login Success`,
  props<{
    session?: Session;
    flow?: LoginFlow;
    requiresOtp?: boolean;
  }>(),
);

export const submitLoginFailure = createAction(
  `${actionPrefix} Submit Login Failure`,
  props<{ error: AuthError }>(),
);

// Registration Flow Actions
export const startRegistrationFlow = createAction(
  `${actionPrefix} Start Registration Flow`,
  props<{ redirectTo?: string | null }>(),
);

export const setRedirectParams = createAction(
  `${actionPrefix} Set Redirect Params`,
  props<{ redirectTo: string | null }>(),
);

export const startRegistrationFlowSuccess = createAction(
  `${actionPrefix} Start Registration Flow Success`,
  props<{ flowId: string; csrfToken: string }>(),
);

export const startRegistrationFlowFailure = createAction(
  `${actionPrefix} Start Registration Flow Failure`,
  props<{ error: AuthError }>(),
);

export const loadRegistrationFlow = createAction(
  `${actionPrefix} Load Registration Flow`,
  props<{ flowId: string }>(),
);

export const loadRegistrationFlowSuccess = createAction(
  `${actionPrefix} Load Registration Flow Success`,
  props<{ flow: RegistrationFlow; csrfToken: string }>(),
);

export const loadRegistrationFlowFailure = createAction(
  `${actionPrefix} Load Registration Flow Failure`,
  props<{ error: AuthError }>(),
);

export const submitRegistration = createAction(
  `${actionPrefix} Submit Registration`,
  props<{
    flowId: string;
    formData: RegistrationFormData;
  }>(),
);

export const submitRegistrationSuccess = createAction(
  `${actionPrefix} Submit Registration Success`,
  props<{
    session?: Session;
    flow?: RegistrationFlow;
    requiresOtp?: boolean;
  }>(),
);

export const submitRegistrationFailure = createAction(
  `${actionPrefix} Submit Registration Failure`,
  props<{ error: AuthError }>(),
);

export const submitOtp = createAction(
  `${actionPrefix} Submit Otp`,
  props<{
    flowId: string;
    flowType: OryFlowType | string;
    csrfToken: string;
    otpFormData: OtpFormData;
    loginData?: LoginFormData;
    registrationData?: RegistrationFormData;
  }>(),
);

export const submitOtpSuccess = createAction(
  `${actionPrefix} Submit Otp Success`,
  props<{
    session?: Session;
    flow?: LoginFlow | RegistrationFlow;
  }>(),
);

export const submitOtpFailure = createAction(
  `${actionPrefix} Submit Otp Failure`,
  props<{ error: AuthError }>(),
);

export const resendOtp = createAction(
  `${actionPrefix} Resend Otp`,
  props<{
    flowId: string;
    flowType: OryFlowType | string;
    csrfToken: string;
    loginData?: LoginFormData;
    registrationData?: RegistrationFormData;
  }>(),
);

export const resendOtpSuccess = createAction(
  `${actionPrefix} Resend Otp Success`,
  props<{
    flow?: LoginFlow | RegistrationFlow;
  }>(),
);

export const resendOtpFailure = createAction(
  `${actionPrefix} Resend Otp Failure`,
  props<{ error: AuthError }>(),
);

export const startResendCountdown = createAction(
  `${actionPrefix} Start Resend Countdown`,
);

export const updateResendCountdown = createAction(
  `${actionPrefix} Update Resend Countdown`,
  props<{ countdown: number }>(),
);

export const removeResendCountdown = createAction(
  `${actionPrefix} Remove Resend Countdown`,
);

export const logout = createAction(`${actionPrefix} Logout`);

export const logoutSuccess = createAction(`${actionPrefix} Logout Success`);

export const logoutFailure = createAction(
  `${actionPrefix} Logout Failure`,
  props<{ error: AuthError }>(),
);

export const navigateToLogin = createAction(
  `${actionPrefix} Navigate To Login`,
  props<{ flowId?: string }>(),
);

export const navigateToRegistration = createAction(
  `${actionPrefix} Navigate To Registration`,
  props<{ flowId?: string }>(),
);

export const navigateToOtpVerify = createAction(
  `${actionPrefix} Navigate To Otp Verify`,
  props<{ flowId: string; email?: string }>(),
);

export const navigateToLanding = createAction(
  `${actionPrefix} Navigate To Landing`,
);

export const navigateToAuthRedirect = createAction(
  `${actionPrefix} Navigate To Auth Redirect`,
  props<{ redirectTo?: string | null }>(),
);

export const clearErrors = createAction(`${actionPrefix} Clear Errors`);

export const clearFlowState = createAction(`${actionPrefix} Clear Flow State`);

export const handleFlowExpired = createAction(
  `${actionPrefix} Handle Flow Expired`,
  props<{ flowType: OryFlowType | string }>(),
);
